import React from 'react';
import { FEDERATION, PGSF_EMBLEM } from '@/lib/api';

function fmtAadhaar(a = '') {
  const d = String(a).replace(/\D/g, '');
  if (!d) return '';
  const pad = d.padEnd(12, '0').slice(0, 12);
  return `${pad.slice(0, 4)}-${pad.slice(4, 8)}-${pad.slice(8, 12)}`;
}
function fmtDOB(iso) {
  if (!iso) return '';
  if (iso.includes('/')) return iso;
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

const NAVY = '#0F1E55';
const RED = '#D93025';

export default function IdCard({ data }) {
  if (!data) return null;
  const addressLine = [data.address, data.district, data.state, data.pincode].filter(Boolean).join(', ');

  return (
    <div id="print-area" className="mx-auto bg-white text-black border border-black/10 shadow-xl overflow-hidden"
         style={{ maxWidth: 820, width: '100%' }}>
      {/* Header */}
      <div className="flex items-center justify-between gap-3 px-4 md:px-6 py-3" style={{ background: NAVY }}>
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-11 w-11 md:h-14 md:w-14 rounded-full bg-white overflow-hidden shrink-0 border-2 border-white">
            <img src={PGSF_EMBLEM} alt="emblem" className="h-full w-full object-cover" />
          </div>
          <div className="min-w-0 text-white">
            <div className="font-bold text-sm md:text-xl leading-tight uppercase tracking-wide truncate">{FEDERATION.name}</div>
            <div className="text-[8px] md:text-[10px] uppercase tracking-widest text-white/80 truncate">{FEDERATION.ministryLine}</div>
          </div>
        </div>
        <div className="text-right text-white/90 shrink-0">
          <div className="text-[8px] md:text-[10px] uppercase tracking-widest">(Reg. {FEDERATION.reg})</div>
        </div>
      </div>

      {/* Front body */}
      <div className="p-4 md:p-6">
        <div className="grid grid-cols-[90px_1fr] md:grid-cols-[140px_1fr] gap-4 md:gap-6">
          {/* Photo + name block */}
          <div>
            <div className="h-[112px] md:h-[170px] w-[90px] md:w-[140px] border border-black/20 bg-white overflow-hidden">
              {data.photo ? <img src={data.photo} alt="player" className="h-full w-full object-cover" /> : <div className="h-full w-full flex items-center justify-center text-[9px] text-black/40 font-mono">NO PHOTO</div>}
            </div>
            <div className="mt-2 font-bold text-sm md:text-base leading-tight break-words">{data.full_name}</div>
            <div className="text-[11px] md:text-xs text-black/70">{data.sport} Player</div>
          </div>

          {/* Fields */}
          <div className="min-w-0">
            <div className="flex items-start justify-end mb-3">
              <div className="text-right">
                <div className="text-[9px] md:text-[10px] uppercase tracking-widest text-black/50">Reg No.</div>
                <div className="font-bold text-sm md:text-base" style={{ color: NAVY }}>{data.reg_no}</div>
              </div>
            </div>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-1 text-[12px] md:text-sm">
              <Row label="Father's Name" value={data.guardian_name} />
              <Row label="Sport" value={data.sport} />
              <Row label="Date of Birth" value={fmtDOB(data.dob)} />
              <Row label="Category" value={data.category} />
              <Row label="State" value={data.state} />
              <Row label="District" value={data.district} />
              <Row label="Event" value={data.event || 'Ist National Championship 2026'} />
              <Row label="Valid for" value="30 Days from registration" />
            </dl>
          </div>
        </div>

        {/* Validity + Trial + Stamp row */}
        <div className="mt-4 md:mt-5 flex flex-wrap items-end justify-between gap-4 border-t border-black/10 pt-4">
          <div>
            <div className="text-[10px] md:text-xs uppercase tracking-widest text-black/60">Valid Till</div>
            <div className="font-bold text-base md:text-lg" style={{ color: NAVY }}>{data.valid_till}</div>
          </div>
          <div className="text-center">
            <div className="font-bold text-base md:text-xl tracking-widest" style={{ color: RED }}>TRIAL ID CARD</div>
            <div className="text-[9px] md:text-[11px] uppercase tracking-widest" style={{ color: NAVY }}>{FEDERATION.name}</div>
          </div>
          <div className="flex items-center gap-3">
            {data.id_qr && <img src={data.id_qr} alt="qr" className="h-14 w-14 md:h-16 md:w-16 border border-black/10 bg-white p-1" />}
            <div className="text-center">
              <div className="h-14 w-14 md:h-16 md:w-16 rounded-full border-2 flex items-center justify-center text-[8px] md:text-[10px] font-bold text-center leading-tight" style={{ borderColor: NAVY, color: NAVY }}>
                PLAYERS<br />SPORTS<br />FEDERATION
              </div>
              <div className="mt-1 text-[8px] md:text-[9px] uppercase tracking-widest text-black/60 border-t border-black/40 pt-0.5">Authorised Signatory</div>
            </div>
          </div>
        </div>
      </div>

      {/* Lower section */}
      <div className="border-t border-black/10 bg-black/5 p-4 md:p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-[12px] md:text-sm mb-3">
          <Row label="Aadhaar" value={fmtAadhaar(data.aadhaar)} />
          <Row label="Mobile" value={data.phone ? `+91 ${data.phone}` : '—'} />
          <Row label="Address" value={addressLine} wrap />
          <Row label="Email" value={data.email} wrap />
        </div>
        <div>
          <div className="text-[10px] md:text-xs uppercase tracking-widest font-bold mb-1" style={{ color: NAVY }}>Terms of Use</div>
          <ol className="list-decimal list-inside space-y-0.5 text-[11px] md:text-xs text-black/80">
            <li>This card identifies a registered player of {FEDERATION.name}.</li>
            <li>The card is personal, non-transferable and must be shown when requested.</li>
            <li>Participation remains subject to registration, payment and document verification.</li>
            <li>Loss or misuse must be reported to the association immediately.</li>
            <li>The association may suspend or cancel this card for misuse.</li>
          </ol>
        </div>
      </div>

      {/* Footer */}
      <div className="text-white text-center px-4 py-2 text-[10px] md:text-xs uppercase tracking-widest" style={{ background: NAVY }}>
        www.playerssportsfederation.com | {FEDERATION.email}
      </div>

      <style>{`
        @media print {
          body * { visibility: hidden; }
          #print-area, #print-area * { visibility: visible; }
          #print-area { position: absolute; left: 0; top: 0; width: 100%; background: #fff; }
        }
      `}</style>
    </div>
  );
}

function Row({ label, value, wrap }) {
  return (
    <div className="min-w-0 py-0.5">
      <span className="font-bold" style={{ color: NAVY }}>{label}:</span>{' '}
      <span className={`text-black ${wrap ? 'break-words' : ''}`}>{value || '—'}</span>
    </div>
  );
}
